import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import {
  IStuErrorCopy,
  STU_ERROR_COPY,
  STU_GENERIC_ERROR,
  STU_NETWORK_ERROR,
} from '@/constants/stuErrorCopy';
import { STU_ERROR_CODES } from '@/constants/superTopupConfig';
import { IStuCommitConflict, IStuErrorBody } from '@/types/stu';

// ---------- Error codes and copy ----------

/**
 * The one read of a failed request. `cause` is set only on PROPOSAL_REVALIDATION_FAILED, where the
 * pay tap reports why; `effectiveCode` is that cause, else the code.
 */
const stuErrorOf = (error: unknown) => {
  const response = (error as AxiosError<IStuErrorBody>)?.response;
  if (!response) return undefined;
  const body = response.data;
  const code = body?.error?.code;
  const cause =
    code === STU_ERROR_CODES.PROPOSAL_REVALIDATION_FAILED
      ? body?.error?.cause
      : undefined;
  return {
    status: response.status,
    body,
    code,
    cause,
    effectiveCode: cause ?? code,
    meta: body?.error?.meta,
  };
};

const hasCode = (error: unknown, code: string) => {
  const parsed = stuErrorOf(error);
  return parsed?.code === code || parsed?.cause === code;
};

export const stuErrorStatus = (error: unknown) => stuErrorOf(error)?.status;

export const stuErrorBody = (error: unknown) => stuErrorOf(error)?.body;

export const stuErrorCode = (error: unknown) => stuErrorOf(error)?.code;

export const stuErrorMessage = (error: unknown) =>
  stuErrorBody(error)?.error?.message;

/** Refused answers: top level on a page save, under error.meta on affirm and submit. */
export const stuAnswerErrors = (error: unknown) => {
  const body = stuErrorBody(error);
  return body?.errors ?? body?.error?.meta?.errors ?? [];
};

/** The version a failed request moved the proposal to: top level on a 422, under error.meta otherwise. */
export const stuErrorVersion = (error: unknown) => {
  const parsed = stuErrorOf(error);
  const version =
    parsed?.status === 422 ? parsed.body?.version : parsed?.meta?.version;
  return typeof version === 'number' ? version : undefined;
};

/**
 * Why the pay tap's PROPOSAL_REVALIDATION_FAILED happened. On POST /payment/initiate it sits flat
 * on `error`, not under `meta` (guide 2026-10-14: the earlier guides were wrong).
 */
export const stuRevalidationCause = (error: unknown) =>
  stuErrorOf(error)?.cause;

/**
 * The insurer couldn't price the plan just now (guide 2026-10-14 §2): keep the screen and offer
 * Try again. The pay tap sends it as `error.cause`.
 */
export const isStuInsurerPriceFailure = (error: unknown) =>
  ([
    STU_ERROR_CODES.INSURER_PRICE_FAILED,
    STU_ERROR_CODES.VENDOR_REQUEST_FAILED,
  ] as string[]).includes(stuErrorOf(error)?.effectiveCode ?? '');

// ---------- Plan ids (guide 2026-10-14 §2) ----------

const PLAN_LIST_CODES: string[] = [
  STU_ERROR_CODES.PLAN_LIST_CHANGED,
  STU_ERROR_CODES.PLAN_NOT_IN_QUOTE,
];

const PLAN_GONE_CODES: string[] = [
  STU_ERROR_CODES.PLAN_NO_LONGER_OFFERED,
  STU_ERROR_CODES.PLAN_NOT_SELECTABLE,
];

/** The saved add-ons can't be priced as they stand; the plan page is where they change. */
const ADDON_CODES: string[] = [
  STU_ERROR_CODES.ADDON_OPTION_REQUIRED,
  STU_ERROR_CODES.ADDON_OPTION_INVALID,
  STU_ERROR_CODES.ADDON_COMBINATION_NOT_ALLOWED,
  STU_ERROR_CODES.ADDON_NOT_APPLIED,
];

export type TStuPlanRefusal =
  | { kind: 'replaced'; planId?: number }
  | { kind: 'updating'; retryAfterMs?: number }
  | { kind: 'reloadList' }
  | { kind: 'gone'; code: string }
  | { kind: 'addons'; code: string };

/**
 * A plan id that can't be used, or a saved selection that can't be priced, from any plan-scoped
 * route. The pay tap sends the code as `error.cause` inside PROPOSAL_REVALIDATION_FAILED.
 */
export const stuPlanRefusal = (
  error: unknown,
): TStuPlanRefusal | undefined => {
  const parsed = stuErrorOf(error);
  const code = parsed?.effectiveCode;
  const meta = parsed?.meta as
    | { planId?: number; retryAfterMs?: number }
    | undefined;
  if (!code) return undefined;

  if (code === STU_ERROR_CODES.PLAN_ID_REPLACED) {
    return { kind: 'replaced', planId: meta?.planId };
  }
  if (code === STU_ERROR_CODES.PLAN_LIST_UPDATING) {
    return { kind: 'updating', retryAfterMs: meta?.retryAfterMs };
  }
  if (PLAN_LIST_CODES.includes(code)) return { kind: 'reloadList' };
  if (PLAN_GONE_CODES.includes(code)) return { kind: 'gone', code };
  if (ADDON_CODES.includes(code)) return { kind: 'addons', code };
  return undefined;
};

/** Affirm and submit refuse a file that must be uploaded again; payment reports it as the cause. */
export const isStuDocumentStale = (error: unknown) =>
  hasCode(error, STU_ERROR_CODES.DOCUMENT_STALE);

/** Affirm and submit refuse saved answers that disagree with KYC; payment reports it as the cause. */
export const isStuIdentityMismatch = (error: unknown) =>
  hasCode(error, STU_ERROR_CODES.KYC_IDENTITY_MISMATCH);

/**
 * Affirm, submit or a payment re-submit found the form incomplete or an answer that isn't one
 * of its choices. Nothing reached the insurer, and a retry can't pass: the customer has to
 * go back to the form. Payment reports it as the cause of a revalidation failure.
 */
export const stuProposalRefusal = (error: unknown) => {
  const parsed = stuErrorOf(error);
  const refusal =
    parsed?.code === STU_ERROR_CODES.PROPOSAL_REVALIDATION_FAILED
      ? parsed.cause
      : parsed?.code;
  return refusal === STU_ERROR_CODES.PROPOSAL_INCOMPLETE ||
    refusal === STU_ERROR_CODES.ANSWER_INVALID
    ? refusal
    : undefined;
};

/**
 * The questions the customer confirmed have changed since (e.g. the insurer dropped one), so the
 * proposal must be confirmed again. Payment reports it as the cause of a revalidation failure.
 */
export const isStuProposalNotAffirmed = (error: unknown) =>
  hasCode(error, STU_ERROR_CODES.PROPOSAL_NOT_AFFIRMED);

/** Our proposal version is behind the server's: re-read the form before anything else. */
export const isStuVersionStale = (code: string | undefined) =>
  code === STU_ERROR_CODES.VERSION_CONFLICT ||
  code === STU_ERROR_CODES.FORM_VERSION_CHANGED;

export const stuIdentityMismatches = (error: unknown) =>
  stuErrorOf(error)?.meta?.mismatches ?? [];

export const stuStaleDocumentKeys = (error: unknown) =>
  (stuErrorOf(error)?.meta?.stale ?? []).map(
    (item) => item.questionKey,
  );

export const stuCommitConflict = (error: unknown) =>
  (error as AxiosError<IStuCommitConflict>)?.response?.data;

/**
 * An add-on whose choices the insurer erased before the 15 Oct fix (`ADDON_OPTION_INVALID` with
 * `meta.choices: []`): every price on the plan is refused and nothing the customer changes can
 * help, so the way on is a fresh quote (guide 2026-10-15 §8).
 */
export const isStuQuoteUnpriceable = (error: unknown) => {
  const parsed = stuErrorOf(error);
  if (parsed?.code !== STU_ERROR_CODES.ADDON_OPTION_INVALID) return false;
  const { choices } = (parsed.meta ?? {}) as { choices?: unknown };
  return Array.isArray(choices) && choices.length === 0;
};

/** Over 7 days old: the journey is over unless meta.payable says it can still be paid. Never retried. */
export const isStuQuoteExpired = (error: unknown) => {
  const parsed = stuErrorOf(error);
  return (
    parsed?.status === 409 && parsed.code === STU_ERROR_CODES.QUOTE_EXPIRED
  );
};

/**
 * Nothing on this quote can change, but its journey card knows where to go: paid
 * (QUOTE_LOCKED), or expired while still payable. Re-read the list and route the card.
 */
export const isStuQuoteLocked = (error: unknown) => {
  const parsed = stuErrorOf(error);
  return (
    parsed?.status === 409 &&
    (parsed.code === STU_ERROR_CODES.QUOTE_LOCKED ||
      (parsed.code === STU_ERROR_CODES.QUOTE_EXPIRED &&
        parsed.meta?.payable === true))
  );
};

export type ResolvedStuError = IStuErrorCopy & { code?: string };

export const resolveStuError = (error: unknown): ResolvedStuError => {
  const code = stuErrorCode(error);
  const mapped = code ? STU_ERROR_COPY[code] : undefined;

  if (mapped) return { code, ...mapped };

  if (code) {
    Sentry.captureMessage('super top-up error code has no copy', {
      level: 'warning',
      extra: { code, status: stuErrorStatus(error) },
    });
  }

  return { code, ...STU_GENERIC_ERROR };
};

export const stuCodeCopy = (code: string | undefined, fallback: string) => {
  const copy = code ? STU_ERROR_COPY[code] : undefined;
  return copy ? [copy.title, copy.body].filter(Boolean).join('. ') : fallback;
};

export const stuToastCopy = (error: unknown, fallback: string) =>
  stuCodeCopy(stuErrorCode(error), fallback);

export const toastStuError = (
  error: unknown,
  fallback: string,
  id: string,
) => toast.error(stuToastCopy(error, fallback), { id });

// ---------- Blocked proposal screens ----------

export const PROPOSAL_NETWORK_ERROR = 'NETWORK_UNAVAILABLE';

export const PROPOSAL_BLOCKING_CODES: string[] = [
  STU_ERROR_CODES.COMPOSITION_CHANGED,
  STU_ERROR_CODES.FORM_UNAVAILABLE,
  STU_ERROR_CODES.PROPOSAL_FROZEN,
];

export interface IProposalBlockedActions {
  choosePlan: () => void;
  retry: () => void;
}

export interface IProposalBlockedCopy {
  title: string;
  body: string;
  actionLabel: string;
  onAction: () => void;
}

const errorCopy = (code: string) => {
  const { title, body = '' } = STU_ERROR_COPY[code];
  return { title, body };
};

export const proposalBlockedCopy = (
  code: string | undefined,
  { choosePlan, retry }: IProposalBlockedActions,
): IProposalBlockedCopy | undefined => {
  if (!code) return undefined;

  return {
    [STU_ERROR_CODES.COMPOSITION_CHANGED]: {
      ...errorCopy(STU_ERROR_CODES.COMPOSITION_CHANGED),
      actionLabel: 'Choose a plan',
      onAction: choosePlan,
    },
    [STU_ERROR_CODES.FORM_UNAVAILABLE]: {
      ...errorCopy(STU_ERROR_CODES.FORM_UNAVAILABLE),
      actionLabel: 'Try again',
      onAction: retry,
    },
    [PROPOSAL_NETWORK_ERROR]: {
      ...STU_NETWORK_ERROR,
      actionLabel: 'Try again',
      onAction: retry,
    },
    [STU_ERROR_CODES.PROPOSAL_FROZEN]: {
      ...errorCopy(STU_ERROR_CODES.PROPOSAL_FROZEN),
      actionLabel: 'Try again',
      onAction: retry,
    },
  }[code];
};
